import React, { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { Backspace, SignOut, Desktop, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PinScreen() {
  const { verifyPin, logout, user, viewMode, setViewMode } = useAuth();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4) {
      submit(pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const submit = async (val) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const emp = await verifyPin(val);
      toast.success(`ΚΑΛΩΣ ΗΡΘΕΣ, ${emp.display_name}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Λάθος PIN";
      setError(typeof msg === "string" ? msg : "Λάθος PIN");
      toast.error(typeof msg === "string" ? msg : "Λάθος PIN");
      // Flash + reset after a moment
      setTimeout(() => {
        setPin("");
        setError("");
      }, 900);
    } finally {
      setLoading(false);
    }
  };

  const push = (d) => {
    setError("");
    setPin((p) => (p.length < 4 ? p + d : p));
  };
  const back = () => {
    setError("");
    setPin((p) => p.slice(0, -1));
  };

  // Keyboard input
  useEffect(() => {
    const onKey = (e) => {
      if (loading) return;
      if (e.key >= "0" && e.key <= "9") push(e.key);
      else if (e.key === "Backspace") back();
      else if (e.key === "Escape") setPin("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 board-bg"
      data-testid="pin-screen"
    >
      <h2 className="font-caveat text-4xl font-bold text-ink leading-none">
        ΚΑΛΩΣ ΗΡΘΕΣ
      </h2>
      <p className="text-[10px] uppercase tracking-[0.25em] text-ink/55 font-bold mt-1 mb-4">
        ΠΛΗΚΤΡΟΛΟΓΗΣΕ ΤΟ PIN ΣΟΥ
      </p>

      {/* Device mode toggle */}
      <div
        className="inline-flex rounded-full border-2 border-ink/15 bg-white/60 p-0.5 mb-6"
        data-testid="device-mode-switch"
      >
        <button
          type="button"
          onClick={() => setViewMode("desktop")}
          data-testid="device-mode-desktop"
          className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] inline-flex items-center gap-1.5 transition-all ${
            viewMode === "desktop"
              ? "bg-ink text-white shadow"
              : "text-ink/65 hover:text-ink"
          }`}
        >
          <Desktop size={12} weight="bold" />
          PC
        </button>
        <button
          type="button"
          onClick={() => setViewMode("mobile")}
          data-testid="device-mode-mobile"
          className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] inline-flex items-center gap-1.5 transition-all ${
            viewMode === "mobile"
              ? "bg-ink text-white shadow"
              : "text-ink/65 hover:text-ink"
          }`}
        >
          <DeviceMobile size={12} weight="bold" />
          ΚΙΝΗΤΟ
        </button>
      </div>

      {/* 4-digit slots */}
      <div className="flex items-center gap-3 mb-6">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            data-testid={`pin-slot-${i}`}
            className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center font-arial text-2xl font-black bg-white/70 transition-all ${
              error
                ? "border-red-400 text-red-700 animate-pulse"
                : pin.length > i
                ? "border-ink text-ink"
                : "border-ink/20 text-ink/30"
            }`}
          >
            {pin.length > i ? "•" : ""}
          </div>
        ))}
      </div>

      {/* Numeric keypad */}
      <div className="grid grid-cols-3 gap-3 w-64">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button
            key={d}
            onClick={() => push(d)}
            disabled={loading}
            data-testid={`pin-key-${d}`}
            className="h-14 rounded-2xl bg-white/80 hover:bg-white border border-ink/15 hover:border-ink/40 text-ink font-arial font-bold text-2xl transition-all active:scale-95 shadow-sm"
          >
            {d}
          </button>
        ))}
        <div />
        <button
          onClick={() => push("0")}
          disabled={loading}
          data-testid="pin-key-0"
          className="h-14 rounded-2xl bg-white/80 hover:bg-white border border-ink/15 hover:border-ink/40 text-ink font-arial font-bold text-2xl transition-all active:scale-95 shadow-sm"
        >
          0
        </button>
        <button
          onClick={back}
          disabled={loading}
          data-testid="pin-key-back"
          className="h-14 rounded-2xl bg-white/60 hover:bg-white border border-ink/15 hover:border-ink/40 text-ink/70 flex items-center justify-center transition-all active:scale-95"
          title="Διαγραφή"
        >
          <Backspace size={22} weight="bold" />
        </button>
      </div>

      <button
        onClick={logout}
        data-testid="pin-logout"
        className="mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-ink/50 hover:text-ink transition-colors"
      >
        <SignOut size={12} weight="bold" />
        ΑΠΟΣΥΝΔΕΣΗ {user?.username ? `(${user.username})` : ""}
      </button>
    </div>
  );
}
